import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { buildHistoryMonthPoints, buildHistoryQuarterPoints } from './shared.js';
import type { TrendView } from './shared.js';
import { monthLabelOf } from './periods.js';
import { TrendViewSwitch } from './TrendViewSwitch.js';
import type { IdtKpiResponse } from '../../../lib/api.js';

/** 目標線 (水色破線) @graph-connects none */
const TARGET_COLOR = '#38bdf8'; /* sky-400 */
/** 実績線 (橙色実線) @graph-connects none */
const ACTUAL_COLOR = '#f97316'; /* orange-500 */

export interface HistoryTrendChartProps {
  /** 最古月〜未来1ヶ月の全レスポンス (月昇順でなくてよい。内部でソートする) */
  responses: (IdtKpiResponse | undefined)[];
  /** JST 当月 "YYYY-MM" (当 Q / 未来判定用) */
  currentMonth: string;
  /** チャート高さ px (compact 配置用)。既定 260 */
  height?: number;
}

interface ChartRow {
  /** 軸表示用の短縮ラベル */
  label: string;
  /** tooltip 用のフルラベル (月 view のみ。Q view は label と同一) */
  fullLabel?: string;
  target: number | null;
  actual: number | null;
  /** 月 view のみ: その月の稼働人数 (Q view では undefined) */
  headcount?: number;
}

interface HistoryTooltipPayload {
  payload?: ChartRow;
}

/** @graph-connects none */
function HistoryTooltip(props: {
  active?: boolean;
  label?: string | number;
  payload?: HistoryTooltipPayload[];
}) {
  const { t } = useTranslation();
  if (!props.active || !props.payload || props.payload.length === 0) return null;
  const row = props.payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-lg text-xs">
      <div className="text-[var(--color-text-muted)] mb-1">{row.fullLabel ?? row.label}</div>
      <div className="tabular-nums" style={{ color: TARGET_COLOR }}>
        {t('idtKpi.historyTrend.target')}: {row.target == null ? '—' : row.target.toLocaleString()}
      </div>
      <div className="tabular-nums" style={{ color: ACTUAL_COLOR }}>
        {t('idtKpi.historyTrend.actual')}: {row.actual == null ? '—' : row.actual.toLocaleString()}
      </div>
      {row.headcount !== undefined && (
        <div className="text-[var(--color-text-muted)] tabular-nums mt-0.5">
          {t('idtKpi.historyTrend.headcount')}: {row.headcount}
          {t('idtKpi.historyTrend.headcountUnit')}
        </div>
      )}
    </div>
  );
}

/**
 * チーム全体の「実績 vs 目標」を月次 / 四半期で振り返る履歴トレンド折線。担当案件明細の下に置く。
 * 月 view は各月、Q view は各 Q を 1 点で描く。目標=水色破線 / 実績=橙色実線。実績ロジックは
 * 上段チャートと同一 (月=完了+処理中、過去Q=完了のみ、当Q=完了+処理中)。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI ページ下部の実績vs目標 履歴トレンド panel。月/四半期切替で全期間の推移を可視化する
 * @graph-connects none
 */
export function HistoryTrendChart({
  responses,
  currentMonth,
  height = 260,
}: HistoryTrendChartProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<TrendView>('month');

  const data = useMemo<ChartRow[]>(() => {
    if (view === 'month') {
      return buildHistoryMonthPoints(responses, currentMonth).map((p) => ({
        // compact 表示: "26/1" 形式 (tooltip では monthLabelOf でフル表示)
        label: `${p.month.slice(2, 4)}/${String(parseInt(p.month.slice(5, 7), 10))}`,
        fullLabel: monthLabelOf(p.month),
        target: p.target,
        actual: p.actual,
        headcount: p.headcount,
      }));
    }
    return buildHistoryQuarterPoints(responses, currentMonth).map((p) => ({
      label: p.label,
      target: p.target,
      actual: p.actual,
    }));
  }, [view, responses, currentMonth]);

  return (
    <div>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 16, right: 16, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="var(--color-border)" strokeDasharray="0" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: 'var(--color-border)' }}
              interval={0}
              minTickGap={0}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={48}
              tickFormatter={(v: number) => v.toLocaleString()}
            />
            <RTooltip
              cursor={{ stroke: 'var(--color-border-hover)', strokeWidth: 1 }}
              content={<HistoryTooltip />}
            />
            <Line
              type="monotone"
              dataKey="target"
              stroke={TARGET_COLOR}
              strokeWidth={1.75}
              strokeDasharray="4 4"
              dot={{
                r: 3,
                fill: 'var(--color-bg-surface)',
                stroke: TARGET_COLOR,
                strokeWidth: 1.5,
              }}
              connectNulls={false}
              isAnimationActive
              animationDuration={1000}
              animationBegin={100}
              animationEasing="ease-out"
            />
            <Line
              type="monotone"
              dataKey="actual"
              stroke={ACTUAL_COLOR}
              strokeWidth={2.25}
              dot={{
                r: 3.5,
                fill: 'var(--color-bg-surface)',
                stroke: ACTUAL_COLOR,
                strokeWidth: 2,
              }}
              activeDot={{
                r: 5,
                fill: 'var(--color-bg-surface)',
                stroke: ACTUAL_COLOR,
                strokeWidth: 2,
              }}
              connectNulls={false}
              isAnimationActive
              animationDuration={1200}
              animationBegin={300}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-[var(--color-text-muted)]">
        <div className="flex items-center gap-1.5">
          <svg width="20" height="2" aria-hidden="true">
            <line
              x1="0"
              y1="1"
              x2="20"
              y2="1"
              stroke={TARGET_COLOR}
              strokeWidth={1.75}
              strokeDasharray="3,3"
            />
          </svg>
          {t('idtKpi.historyTrend.target')}
        </div>
        <div className="flex items-center gap-1.5">
          <svg width="20" height="2" aria-hidden="true">
            <line x1="0" y1="1" x2="20" y2="1" stroke={ACTUAL_COLOR} strokeWidth={2.25} />
          </svg>
          {t('idtKpi.historyTrend.actual')}
        </div>
        <div className="ml-auto">
          <TrendViewSwitch view={view} onChange={setView} />
        </div>
      </div>
    </div>
  );
}
